/**
 * The ONE role name the framework acts on. Everything else — editor, marketing,
 * whatever a site needs — is declared by the application, not here.
 *
 * Admin bypasses every check, which is why no section ever lists it.
 */
export const ADMIN_ROLE = 'admin';

/**
 * Audience sentinels.
 *   ALL  — everyone, including anonymous visitors.
 *   NONE — nobody except admin (who always bypasses).
 */
export const ALL = 'ALL' as const;
export const NONE = 'NONE' as const;

export type Audience = readonly string[];

/** Defaults when a section says nothing: anyone may read, nobody may write. */
export const DEFAULT_VIEW: Audience = [ALL];
export const DEFAULT_EDIT: Audience = [NONE];

/** Admin bypasses everything. Hard-coded, not wildcard rows — can't be revoked by accident. */
function granted(roles: readonly string[], audience: Audience): boolean {
  if (roles.includes(ADMIN_ROLE)) return true;
  if (audience.includes(NONE)) return false;
  if (audience.includes(ALL)) return true;
  return audience.some((r) => roles.includes(r));
}

/** Who may see the section at all. Default `[ALL]`. */
export const canView = (roles: readonly string[], viewableBy: Audience = DEFAULT_VIEW): boolean =>
  granted(roles, viewableBy);

/**
 * Who may edit it. Default `[NONE]` — a section is read-only until it explicitly
 * grants write access. Unauthenticated writes are refused before this is reached.
 *
 * NO ROLES ⇒ NO WRITE, always. A signed-in account with an empty role list has been
 * approved but not yet granted anything, and must be treated exactly like a visitor
 * for editing purposes — including against a section whose audience is [ALL], which
 * otherwise would have let any logged-in user through.
 */
export function canWrite(roles: readonly string[], editableBy: Audience = DEFAULT_EDIT): boolean {
  if (roles.includes(ADMIN_ROLE)) return true;
  if (roles.length === 0) return false;
  return granted(roles, editableBy);
}

/* -------------------------------------------------------------------------- */
/*  Denial object — cms.get() returns this instead of throwing                 */
/* -------------------------------------------------------------------------- */

export const PERMISSION_NOT_GRANTED = 'PermissionNotGranted' as const;

export interface PermissionNotGrantedObject {
  readonly kind: typeof PERMISSION_NOT_GRANTED;
  readonly section: string;
  readonly reason: 'FORBIDDEN' | 'NOT_FOUND';
  readonly message: string;
}

export const permissionNotGranted = (
  section: string,
  reason: 'FORBIDDEN' | 'NOT_FOUND' = 'FORBIDDEN',
): PermissionNotGrantedObject => ({
  kind: PERMISSION_NOT_GRANTED,
  section,
  reason,
  message: reason === 'FORBIDDEN'
    ? `You do not have permission to view "${section}".`
    : `Section "${section}" does not exist.`,
});

export const isPermissionNotGranted = (v: unknown): v is PermissionNotGrantedObject =>
  typeof v === 'object' && v !== null && (v as { kind?: string }).kind === PERMISSION_NOT_GRANTED;
